import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FlowAssistantChatService,
  FlowChatCitation,
  FlowChatTurn,
} from "src/app/Services/flow-assistant-chat.service";

interface ChatLine {
  role: "user" | "assistant";
  content: string;
  citations?: FlowChatCitation[];
}

@Component({
  selector: "app-flow-assistant-chat",
  templateUrl: "./flow-assistant-chat.component.html",
  styleUrls: ["./flow-assistant-chat.component.scss"],
})
export class FlowAssistantChatComponent implements OnInit, OnChanges {
  private static readonly SessionStorageKey = "flow-assistant-chat-session-id";
  @Input() flowData: any;
  @Input() embeddedInBlogs: any[] = [];
  @Output() closeRequested = new EventEmitter<void>();
  isMobileView = typeof window !== "undefined" ? window.innerWidth < 768 : false;
  panelOpen = true;
  inputText = "";
  loading = false;
  clearingHistory = false;
  showDeleteConfirm = false;
  error: string | null = null;
  lines: ChatLine[] = [];
  askFromDocument = false;

  get supportingDocuments(): { documentName: string; documentUrl: string }[] {
    const docs =
      this.flowData?.flowSupportingDocumentsDocumentModels ||
      this.flowData?.FlowSupportingDocumentsDocumentModels ||
      [];
    return Array.isArray(docs)
      ? docs.filter((d: any) => d?.documentUrl)
      : [];
  }

  get hasDocuments(): boolean {
    return this.supportingDocuments.length > 0;
  }

  get featuredBlogs(): any[] {
    return Array.isArray(this.embeddedInBlogs) ? this.embeddedInBlogs : [];
  }

  get hasFeaturedBlogs(): boolean {
    return this.featuredBlogs.length > 0;
  }

  getBlogTitle(blog: any): string {
    return (blog?.title || blog?.Title || "Untitled blog").toString();
  }

  getBlogAuthor(blog: any): string {
    return (blog?.authorName || blog?.AuthorName || "").toString();
  }

  getBlogCover(blog: any): string | null {
    return blog?.coverImage || blog?.CoverImage || null;
  }

  toggleAskFromDocument(): void {
    if (!this.hasDocuments) {
      return;
    }
    this.askFromDocument = !this.askFromDocument;
  }

  constructor(
    private flowChat: FlowAssistantChatService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadHistoryFromDb();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["flowData"] && !changes["flowData"].firstChange) {
      this.askFromDocument = false;
      this.loadHistoryFromDb();
    }
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (typeof window === "undefined") {
      return;
    }
    this.isMobileView = window.innerWidth < 768;
  }

  openPanel(): void {
    this.panelOpen = true;
  }

  closePanel(): void {
    this.panelOpen = false;
    this.closeRequested.emit();
  }

  openDeleteConfirm(): void {
    this.showDeleteConfirm = true;
  }

  closeDeleteConfirm(): void {
    if (this.clearingHistory) {
      return;
    }
    this.showDeleteConfirm = false;
  }

  clearChat(): void {
    this.clearingHistory = true;
    this.flowChat
      .clearHistory(this.getFlowRowKey(), this.getSessionId())
      .subscribe({
        next: () => {
          this.lines = [];
          this.error = null;
          this.clearingHistory = false;
          this.showDeleteConfirm = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.clearingHistory = false;
          this.error = "Unable to clear the conversation right now.";
          this.cdr.detectChanges();
        },
      });
  }

  send(): void {
    const text = (this.inputText || "").trim();
    if (!text || this.loading) {
      return;
    }

    this.inputText = "";
    this.lines.push({ role: "user", content: text });
    this.error = null;
    this.loading = true;

    const history: FlowChatTurn[] = this.lines
      .slice(0, -1)
      .map((l) => ({ role: l.role, content: l.content }));

    let rawFlowDataJson: string | undefined;
    let flowRowKey: string | undefined;
    if (this.flowData != null) {
      try {
        rawFlowDataJson = JSON.stringify(this.flowData);
      } catch {
        rawFlowDataJson = undefined;
      }
      flowRowKey = this.flowData?.rowKey || this.flowData?.RowKey;
    }

    const documentUrls =
      this.askFromDocument && this.hasDocuments
        ? this.supportingDocuments.map((d) => d.documentUrl)
        : undefined;

    this.flowChat
      .ask({
        message: text,
        history,
        rawFlowDataJson,
        flowRowKey,
        sessionId: this.getSessionId(),
        documentUrls,
      })
      .subscribe({
        next: (res) => {
          const answer = (res?.answer || "").trim();
          const citations = res?.citations?.length ? res.citations : undefined;

          this.lines.push({
            role: "assistant",
            content: answer,
            citations,
          });
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loading = false;
          const msg =
            err?.error?.error ||
            err?.message ||
            "Something went wrong. Please try again.";
          this.error = typeof msg === "string" ? msg : "Request failed.";
        },
      });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  sendSuggestedPrompt(prompt: string): void {
    if (this.loading || !prompt?.trim()) {
      return;
    }

    this.inputText = prompt.trim();
    this.send();
  }

  askAboutFeaturedBlogs(): void {
    this.sendSuggestedPrompt(
      "Is this flow featured in any blogs? Summarize those blogs."
    );
  }

  askAboutBlog(blog: any): void {
    const title = this.getBlogTitle(blog);
    this.sendSuggestedPrompt(
      `Tell me about the blog "${title}" that features this flow.`
    );
  }

  private getFlowRowKey(): string | undefined {
    return this.flowData?.rowKey || this.flowData?.RowKey;
  }

  private getSessionId(): string | undefined {
    if (typeof window === "undefined") {
      return undefined;
    }

    const existing = window.localStorage.getItem(FlowAssistantChatComponent.SessionStorageKey);
    if (existing) {
      return existing;
    }

    const generated = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(FlowAssistantChatComponent.SessionStorageKey, generated);
    return generated;
  }

  private loadHistoryFromDb(): void {
    this.flowChat.getHistory(this.getFlowRowKey(), this.getSessionId()).subscribe({
      next: (res) => {
        const history = Array.isArray(res?.history) ? res.history : [];
        this.lines = history.map((h) => ({
          role: h.role === "assistant" ? "assistant" : "user",
          content: h.content || "",
        }));
        this.cdr.detectChanges();
      },
      error: () => {
        this.lines = [];
      },
    });
  }
}
